// XZ3D WFC tile-skin core — pure solver, no THREE, no DOM.
//
// Wave Function Collapse over the shape-blob union (XZ3D_WFC_TILE_POC_HANDOFF_20260802.md).
// TWO-LAYER RULE (non-negotiable): the shape blob is the MACRO layer and the rules
// truth — this module only SKINS the union with 5 ft presentation tiles. Every
// per-cell constraint below is DERIVED from the blob (walls, openings), so a WFC
// contradiction or re-solve can never move a wall Janus enforces. Mirrors the
// ft3d-shape-core.js discipline: pure functions, deterministic, TS-port prototype.
//
// Socket model (single-char labels; compatibility = equality across an edge):
//   'o' open floor edge   'w' wall runs along this edge (geometry faces out)
//   'd' doorway/threshold 'c' canal — water crosses this edge
// Cell-edge requirements derived from the blob:
//   wall edge (union boundary / unopened face / impassable opening) → socket 'w'
//   door edge (passable opening between two cells)                  → 'd' or 'c'
//   open edge (plain interior adjacency)                            → 'o' or 'c'
// 'w'/'d' are FORBIDDEN on open edges, so the skin can never invent a
// freestanding wall or doorway the rules don't have.

import { insideUnion, segmentHit } from './ft3d-shape-core.js';

const EPS = 1e-6;
const STAND_Z = 0.5; // same ankle-height crossing convention as the shape lab

// Direction order everywhere: [N(+y), E(+x), S(-y), W(-x)].
export const DIRS = [
  { dx: 0, dy: 1 }, { dx: 1, dy: 0 }, { dx: 0, dy: -1 }, { dx: -1, dy: 0 },
];
export const OPPOSITE = [2, 3, 0, 1];

// ---- Seeded PRNG (mulberry32 over a string hash): same seed + same
// placements ⇒ identical output, asserted in the smoke. A solved dungeon can
// therefore persist as seed + overrides (the encounter_backdrop_seed pattern).

export function hashString(text) {
  let h = 1779033703 ^ String(text).length;
  for (let i = 0; i < String(text).length; i++) {
    h = Math.imul(h ^ String(text).charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

export function mulberry32(seedInt) {
  let a = seedInt >>> 0;
  return function next() {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function now() {
  return globalThis.performance?.now?.() ?? Date.now();
}

// ---- Tile set v0 — fully programmatic placeholders (no asset files).
//
// Structural completeness is load-bearing: every combination of edge
// requirements a blob can produce ({open, wall, door}^4) has at least one dry
// tile, so the solver cannot be starved by geometry — only by water routing,
// which restarts handle. Water variants overlay 'c' onto non-wall edges
// (endcap / straight / bend, optional tee) so canals must connect or
// terminate — the video's constraint made visible.

function rotateTuple(tuple, rotation) {
  return tuple.map((_, index) => tuple[(index - rotation + 4) % 4]);
}

function canonicalTuple(tuple) {
  // Asset/catalog IDs put the most structural socket first so authoring names
  // remain readable and stable: canal, then door, then wall, then open. This
  // yields t-wooo/t-wwoo/t-wowo for the common cave wall classes instead of
  // open-first spellings such as t-ooow.
  const rank = { c: 0, d: 1, w: 2, o: 3 };
  let best = null;
  let bestRank = null;
  for (let rotation = 0; rotation < 4; rotation++) {
    const candidate = rotateTuple(tuple, rotation).join('');
    const candidateRank = [...candidate].map(socket => rank[socket]).join('');
    if (best === null || candidateRank < bestRank) {
      best = candidate;
      bestRank = candidateRank;
    }
  }
  return best;
}

const FLOOR_WEIGHTS = [10, 6, 3.5, 2, 1.2];
const VARIANT_LETTERS = 'abcde';

export function makeTileSet({
  floorVariants = 3, wallVariants = 2, clutter = 1, waterTee = false, waterWeight = 1,
  environmentFlavor = false, environmentDressing = false,
} = {}) {
  const tiles = [];
  const floors = Math.max(1, Math.min(5, Math.floor(floorVariants)));
  const wallLooks = Math.max(1, Math.min(5, Math.floor(wallVariants)));

  for (let index = 0; index < floors; index++) {
    tiles.push({
      id: `floor-${VARIANT_LETTERS[index]}`,
      sockets: ['o', 'o', 'o', 'o'],
      weight: FLOOR_WEIGHTS[index],
      family: 'floor',
      variantIndex: index,
    });
  }
  if (clutter > 0) {
    tiles.push({ id: 'rubble', sockets: ['o', 'o', 'o', 'o'], weight: 1.2 * clutter, family: 'rubble', variantIndex: 0 });
    tiles.push({ id: 'column', sockets: ['o', 'o', 'o', 'o'], weight: 0.5 * clutter, family: 'column', variantIndex: 0 });
    if (environmentDressing) {
      // Dressing competes only for open cells and stays deliberately sparse.
      // Open-socket quarter-turns are selected per cell by the renderer, so a
      // single authored cluster does not advertise the five-foot tile grid.
      tiles.push(
        { id: 'decor-stalagmites', sockets: ['o', 'o', 'o', 'o'], weight: 0.34 * clutter, family: 'decor', variantIndex: 0 },
        { id: 'decor-slab-drift', sockets: ['o', 'o', 'o', 'o'], weight: 0.52 * clutter, family: 'decor', variantIndex: 1 },
        { id: 'decor-fungal-ring', sockets: ['o', 'o', 'o', 'o'], weight: 0.28 * clutter, family: 'decor', variantIndex: 2 },
      );
    }
  }

  const maxWater = waterTee ? 3 : 2;
  const symbols = ['o', 'w', 'd'];
  const seen = new Set();
  for (let code = 0; code < 81; code++) {
    const dry = [
      symbols[code % 3],
      symbols[Math.floor(code / 3) % 3],
      symbols[Math.floor(code / 9) % 3],
      symbols[Math.floor(code / 27) % 3],
    ];
    const wetPositions = [0, 1, 2, 3].filter(position => dry[position] !== 'w');
    for (let mask = 0; mask < (1 << wetPositions.length); mask++) {
      const waterCount = wetPositions.filter((_, bit) => mask & (1 << bit)).length;
      if (waterCount > maxWater) continue;
      const tuple = dry.slice();
      wetPositions.forEach((position, bit) => {
        if (mask & (1 << bit)) tuple[position] = 'c';
      });
      const canon = canonicalTuple(tuple);
      if (canon === 'oooo' || seen.has(canon)) continue;
      seen.add(canon);
      const sockets = canon.split('');
      const walls = sockets.filter(s => s === 'w').length;
      const doors = sockets.filter(s => s === 'd').length;
      const water = sockets.filter(s => s === 'c').length;
      // Water weights are low in AGGREGATE: the family has many rotations, and
      // every one of them competes in every open cell — tuned so spontaneous
      // canals are a rare garnish and real canals come from GM placement.
      // Endcaps outweigh continuations so a canal prefers to terminate.
      const weight = water > 0
        ? (water === 1 ? 0.12 : water === 2 ? 0.04 : 0.02) * waterWeight
        : 4 / (1 + doors);
      const family = water > 0 ? 'water' : (walls > 0 ? 'wall' : 'door');
      tiles.push({ id: `t-${canon}`, sockets, weight, family, variantIndex: 0 });
      // Visual look-variants for the plain wall classes only — they are what
      // repeats along every perimeter, so they drive the variety budget.
      if (water === 0 && doors === 0 && walls > 0) {
        for (let look = 1; look < wallLooks; look++) {
          tiles.push({
            id: `t-${canon}-${VARIANT_LETTERS[look]}`,
            sockets,
            weight: weight / (look + 1.5),
            family: 'wall',
            variantIndex: look,
          });
        }
      }
    }
  }
  // Proof-only architectural modules are zero-weight: the stable WFC solve is
  // completed first, then the lab places each module once at a recorded cell.
  // This proves game-camera readability without teaching the solver to stamp a
  // high-salience motif on every five-foot wall tile.
  if (environmentFlavor) {
    tiles.push(
      { id: 'flavor-buttress', sockets: ['w', 'o', 'o', 'o'], weight: 0, family: 'wall', variantIndex: 5 },
      { id: 'flavor-niche', sockets: ['w', 'o', 'o', 'o'], weight: 0, family: 'wall', variantIndex: 6 },
      { id: 'flavor-damaged', sockets: ['w', 'o', 'o', 'o'], weight: 0, family: 'wall', variantIndex: 7 },
      { id: 'flavor-arch-reinforced', sockets: ['d', 'o', 'o', 'o'], weight: 0, family: 'door', variantIndex: 1 },
    );
  }
  return {
    tiles,
    options: {
      floorVariants: floors, wallVariants: wallLooks, clutter, waterTee, waterWeight,
      environmentFlavor, environmentDressing,
    },
  };
}

// Rotations expand programmatically; symmetric tiles (e.g. all-open) collapse
// to their distinct socket-tuples only. Rotation is presentation data — the
// renderer applies it per instance, so it never multiplies draw calls.
export function expandVariants(tileSet) {
  const variants = [];
  for (const tile of tileSet.tiles) {
    const seenRotations = new Set();
    for (let rotation = 0; rotation < 4; rotation++) {
      const sockets = rotateTuple(tile.sockets, rotation);
      const key = sockets.join('');
      if (seenRotations.has(key)) continue;
      seenRotations.add(key);
      variants.push({
        id: `${tile.id}@${rotation}`,
        tileId: tile.id,
        rotation,
        sockets,
        weight: tile.weight,
        family: tile.family,
        variantIndex: tile.variantIndex,
      });
    }
  }
  return variants;
}

// ---- Cell field: rasterize the union into cellFt cells (center-inside test)
// and classify every edge from the blob. The grid anchors to the bbox snapped
// to cellFt so cell borders land on multiples of cellFt; box faces that sit
// off-grid render up to cellFt/2 inset — a declared placeholder-skin
// limitation, the RULES still use the exact blob.

function crossesPassableOpening(shape, x1, y1, x2, y2, z = STAND_Z) {
  const openings = shape.openings ?? [];
  const axis = Math.abs(x2 - x1) > Math.abs(y2 - y1) ? 'x' : 'y';
  const [from, to] = axis === 'x' ? [x1, x2] : [y1, y2];
  const low = Math.min(from, to) + EPS;
  const high = Math.max(from, to) - EPS;
  const spanCoord = axis === 'x' ? y1 : x1;
  return openings.some(opening =>
    opening.plane.axis === axis && opening.passable !== false
    && opening.plane.at > low && opening.plane.at < high
    && spanCoord >= opening.span.min - EPS && spanCoord <= opening.span.max + EPS
    && z >= opening.z.min - EPS && z <= opening.z.max + EPS);
}

export function buildCellField(shape, cellFt = 5) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const box of shape.boxes) {
    minX = Math.min(minX, box.x.min);
    minY = Math.min(minY, box.y.min);
    maxX = Math.max(maxX, box.x.max);
    maxY = Math.max(maxY, box.y.max);
  }
  const originX = Math.floor(minX / cellFt) * cellFt;
  const originY = Math.floor(minY / cellFt) * cellFt;
  const cols = Math.ceil((maxX - originX) / cellFt);
  const rows = Math.ceil((maxY - originY) / cellFt);

  const cells = [];
  const byKey = new Map();
  for (let iy = 0; iy < rows; iy++) {
    for (let ix = 0; ix < cols; ix++) {
      const cx = originX + (ix + 0.5) * cellFt;
      const cy = originY + (iy + 0.5) * cellFt;
      if (!insideUnion(shape, cx, cy)) continue;
      const cell = { key: `${ix},${iy}`, ix, iy, cx, cy, edges: [], neighbors: [] };
      cells.push(cell);
      byKey.set(cell.key, cell);
    }
  }
  for (const cell of cells) {
    for (let direction = 0; direction < 4; direction++) {
      const nx = cell.cx + DIRS[direction].dx * cellFt;
      const ny = cell.cy + DIRS[direction].dy * cellFt;
      const neighborKey = `${cell.ix + DIRS[direction].dx},${cell.iy + DIRS[direction].dy}`;
      const neighbor = byKey.get(neighborKey) ?? null;
      if (!neighbor
        || segmentHit(shape, cell.cx, cell.cy, nx, ny, { z: STAND_Z, mode: 'move' }) !== null) {
        cell.edges[direction] = 'wall';
        cell.neighbors[direction] = null;
        continue;
      }
      cell.edges[direction] = crossesPassableOpening(shape, cell.cx, cell.cy, nx, ny) ? 'door' : 'open';
      cell.neighbors[direction] = neighborKey;
    }
  }
  return { origin: { x: originX, y: originY }, cellFt, cols, rows, cells, byKey };
}

const EDGE_ALLOWS = {
  wall: socket => socket === 'w',
  door: socket => socket === 'd' || socket === 'c',
  open: socket => socket === 'o' || socket === 'c',
};

export function variantAllowedAt(variant, cell) {
  return cell.edges.every((edge, direction) => EDGE_ALLOWS[edge](variant.sockets[direction]));
}

// ---- Solver: min-entropy collapse with support propagation, bounded restarts.

function weightedPick(rng, domain, variants) {
  let total = 0;
  for (const index of domain) total += variants[index].weight;
  let roll = rng() * total;
  for (const index of domain) {
    roll -= variants[index].weight;
    if (roll <= 0) return index;
  }
  return domain[domain.length - 1];
}

export function solveShapeSkin(shape, {
  cellFt = 5,
  seed = 'wfc',
  placements = {},
  tileSet = makeTileSet(),
  maxAttempts = 64,
} = {}) {
  const startedAt = now();
  const field = buildCellField(shape, cellFt);
  const variants = expandVariants(tileSet);
  const variantIndexById = new Map(variants.map((variant, index) => [variant.id, index]));
  const cellIndexByKey = new Map(field.cells.map((cell, index) => [cell.key, index]));

  const baseDomains = field.cells.map(cell => {
    const domain = [];
    for (let index = 0; index < variants.length; index++) {
      if (variantAllowedAt(variants[index], cell)) domain.push(index);
    }
    if (!domain.length) {
      throw new Error(`wfc: tile set has no variant for cell ${cell.key} (edges ${cell.edges.join(',')})`);
    }
    return domain;
  });

  const pinned = new Map();
  for (const [key, variantId] of Object.entries(placements)) {
    const cellIndex = cellIndexByKey.get(key);
    if (cellIndex === undefined) throw new Error(`wfc: placement targets unknown cell ${key}`);
    const variantIndex = variantIndexById.get(variantId);
    if (variantIndex === undefined) throw new Error(`wfc: placement variant ${variantId} not in tile set`);
    if (!baseDomains[cellIndex].includes(variantIndex)) {
      throw new Error(`wfc: placement ${variantId} violates cell ${key} edge constraints (${field.cells[cellIndex].edges.join(',')})`);
    }
    pinned.set(cellIndex, variantIndex);
  }

  const propagate = (domains, queue) => {
    while (queue.length) {
      const cellIndex = queue.pop();
      const cell = field.cells[cellIndex];
      for (let direction = 0; direction < 4; direction++) {
        const neighborKey = cell.neighbors[direction];
        if (!neighborKey) continue;
        const neighborIndex = cellIndexByKey.get(neighborKey);
        const supported = new Set();
        for (const index of domains[cellIndex]) supported.add(variants[index].sockets[direction]);
        const opposite = OPPOSITE[direction];
        const before = domains[neighborIndex].length;
        const filtered = domains[neighborIndex].filter(index => supported.has(variants[index].sockets[opposite]));
        if (!filtered.length) return false;
        if (filtered.length !== before) {
          domains[neighborIndex] = filtered;
          queue.push(neighborIndex);
        }
      }
    }
    return true;
  };

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const rng = mulberry32(hashString(`${seed}#${attempt}`));
    const domains = baseDomains.map(domain => domain.slice());
    for (const [cellIndex, variantIndex] of pinned) domains[cellIndex] = [variantIndex];
    if (!propagate(domains, field.cells.map((_, index) => index))) continue;

    let contradiction = false;
    for (;;) {
      let minSize = Infinity;
      const candidates = [];
      for (let index = 0; index < domains.length; index++) {
        const size = domains[index].length;
        if (size <= 1) continue;
        if (size < minSize) {
          minSize = size;
          candidates.length = 0;
        }
        if (size === minSize) candidates.push(index);
      }
      if (!candidates.length) break; // fully collapsed
      const cellIndex = candidates[Math.floor(rng() * candidates.length)];
      const choice = weightedPick(rng, domains[cellIndex], variants);
      domains[cellIndex] = [choice];
      if (!propagate(domains, [cellIndex])) {
        contradiction = true;
        break;
      }
    }
    if (contradiction) continue;

    const grid = {};
    field.cells.forEach((cell, index) => {
      grid[cell.key] = variants[domains[index][0]].id;
    });
    const usedTypes = new Set(Object.values(grid).map(id => variants[variantIndexById.get(id)].tileId));
    return {
      shape,
      cellFt,
      origin: field.origin,
      seed,
      placements: { ...placements },
      tileSet,
      cells: field.cells,
      grid,
      attempts: attempt,
      stats: {
        cellCount: field.cells.length,
        variantCatalog: variants.length,
        typeCatalog: tileSet.tiles.length,
        usedTypes: usedTypes.size,
        usedVariants: new Set(Object.values(grid)).size,
        solveMs: now() - startedAt,
      },
    };
  }
  throw new Error(`wfc: no solution within ${maxAttempts} attempts (seed ${seed})`);
}

// ---- Validation: recompute every constraint from the blob + the solved grid.
// The smoke runs this after solve, place, and perturb.

export function validateSolution(solution) {
  const errors = [];
  const variants = expandVariants(solution.tileSet);
  const variantById = new Map(variants.map(variant => [variant.id, variant]));

  // Recompute the cell field FROM THE BLOB — validating against the
  // solution's own cached cells would let a classifier bug self-certify
  // (Codex finding, 2026-08-02). Every check below runs on the fresh field.
  const field = buildCellField(solution.shape, solution.cellFt);
  const freshByKey = new Map(field.cells.map(cell => [cell.key, cell]));

  const cachedByKey = new Map(solution.cells.map(cell => [cell.key, cell]));
  if (solution.cells.length !== field.cells.length) {
    errors.push(`solution caches ${solution.cells.length} cells, blob recompute has ${field.cells.length}`);
  }
  for (const cell of solution.cells) {
    const fresh = freshByKey.get(cell.key);
    if (!fresh) {
      errors.push(`${cell.key}: cached cell absent from blob recompute`);
    } else if (fresh.edges.join(',') !== cell.edges.join(',')
      || fresh.neighbors.join(',') !== cell.neighbors.join(',')) {
      errors.push(`${cell.key}: cached edges/neighbors disagree with blob recompute`);
    }
  }
  const gridKeys = Object.keys(solution.grid);
  if (gridKeys.length !== field.cells.length) {
    errors.push(`grid has ${gridKeys.length} entries for ${field.cells.length} cells`);
  }
  for (const key of gridKeys) {
    if (!freshByKey.has(key)) errors.push(`grid entry for unknown cell ${key}`);
  }
  if (!cachedByKey.size) errors.push('solution has no cells');

  for (const cell of field.cells) {
    const variantId = solution.grid[cell.key];
    const variant = variantById.get(variantId);
    if (!variant) {
      errors.push(`${cell.key}: missing/unknown variant ${variantId}`);
      continue;
    }
    for (let direction = 0; direction < 4; direction++) {
      const socket = variant.sockets[direction];
      const edge = cell.edges[direction];
      if (!EDGE_ALLOWS[edge](socket)) {
        errors.push(`${cell.key}: socket ${socket} illegal on ${edge} edge (dir ${direction})`);
      }
      const neighborKey = cell.neighbors[direction];
      if (!neighborKey) {
        // Water may never point at a wall — covered by EDGE_ALLOWS, restated
        // for the "canal never dead-ends unterminated" guarantee.
        if (socket === 'c') errors.push(`${cell.key}: canal dead-ends into wall (dir ${direction})`);
        continue;
      }
      if (direction > 1) continue; // each pair checked once (N/E owner)
      const neighborVariant = variantById.get(solution.grid[neighborKey]);
      if (!neighborVariant) continue;
      const facing = neighborVariant.sockets[OPPOSITE[direction]];
      if (facing !== socket) {
        errors.push(`${cell.key}→${neighborKey}: socket mismatch ${socket} vs ${facing}`);
      }
    }
  }
  return { ok: errors.length === 0, errors };
}

// ---- GM interaction verbs.

// Re-solve honoring prior GM placements plus new ones ("generate around
// placed tiles"). Same seed ⇒ the untouched surroundings stay familiar.
export function resolveAround(solution, placements) {
  return solveShapeSkin(solution.shape, {
    cellFt: solution.cellFt,
    seed: solution.seed,
    placements: { ...solution.placements, ...placements },
    tileSet: solution.tileSet,
  });
}

// Swap one cell to a random different legal tile, re-solving only its
// neighborhood (growing the frozen-ring radius until it solves). The rest of
// the dungeon is pinned so GM-approved areas never churn.
export function perturb(solution, cellKey, seed = 'perturb') {
  const cell = solution.cells.find(candidate => candidate.key === cellKey);
  if (!cell) throw new Error(`wfc: perturb targets unknown cell ${cellKey}`);
  const variants = expandVariants(solution.tileSet);
  const rng = mulberry32(hashString(`${solution.seed}~${seed}~${cellKey}`));
  const currentId = solution.grid[cellKey];
  const candidates = variants.filter(variant => variant.id !== currentId && variantAllowedAt(variant, cell));
  if (!candidates.length) {
    return { solution, radius: 0, attempts: 0, changedCells: 0, reason: 'no-alternative' };
  }
  // Deterministic shuffle, then try several candidates per ring radius — a
  // single unlucky forced tile must not fail a perturb that another legal
  // alternative would solve (Codex finding, 2026-08-02).
  for (let index = candidates.length - 1; index > 0; index--) {
    const swap = Math.floor(rng() * (index + 1));
    [candidates[index], candidates[swap]] = [candidates[swap], candidates[index]];
  }
  const tryList = candidates.slice(0, 4);
  // Perturbing a GM-placed cell REPLACES that placement (the perturb verb is
  // explicit GM intent too) — surfaced to the caller, never silent.
  const replacedPlacement = Object.prototype.hasOwnProperty.call(solution.placements, cellKey);

  for (const radius of [2, 3, 4, Infinity]) {
    for (const candidate of tryList) {
      const pins = {};
      for (const other of solution.cells) {
        const distance = Math.max(Math.abs(other.ix - cell.ix), Math.abs(other.iy - cell.iy));
        if (distance > radius) pins[other.key] = solution.grid[other.key];
      }
      Object.assign(pins, solution.placements); // GM intent survives inside the ring
      pins[cellKey] = candidate.id;
      try {
        const next = solveShapeSkin(solution.shape, {
          cellFt: solution.cellFt,
          seed: `${solution.seed}~${seed}`,
          placements: pins,
          tileSet: solution.tileSet,
          maxAttempts: 12,
        });
        // Keep only the durable intent on the result: original GM placements
        // plus the perturbed cell (the frozen ring was scaffolding).
        next.placements = { ...solution.placements, [cellKey]: candidate.id };
        let changedCells = 0;
        for (const key of Object.keys(next.grid)) {
          if (next.grid[key] !== solution.grid[key]) changedCells++;
        }
        return {
          solution: next, radius, attempts: next.attempts, changedCells,
          forced: candidate.id, replacedPlacement,
        };
      } catch {
        // contradiction with this candidate inside this ring — try the next
      }
    }
  }
  throw new Error(`wfc: perturb could not re-solve around ${cellKey}`);
}
